import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import logo from '../assets/brand/logo.png';
import {
  loginErrorMessage,
  requestLoginCode,
  storeWebSession,
  verifyLoginCode,
} from '../lib/webSession';

const TG_LINK =
  (import.meta.env.VITE_TG_LINK as string | undefined) || 'https://t.me/incpt_wallet/app';

/** Clearance so the submit button sits above the keys and the iOS autofill bar. */
const KEYBOARD_GAP = 16;

/**
 * Sign-in for the browser (email OTP). After login the app boots the same
 * wallet create/import flow as TMA.
 */
export function WebLogin({ onSignedIn }: { onSignedIn: () => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const columnRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [lift, setLift] = useState(0);

  // Browser only — this screen is not mounted inside Telegram. iOS Safari
  // overlays the keyboard, so the centered card stays put and the button slides
  // under the keys. Measure the card without our own shift, then move the
  // column up by however much of it is still covered.
  useEffect(() => {
    const vv = window.visualViewport;
    const column = columnRef.current;
    if (!vv || !column) return;

    let settle = 0;

    const apply = (next: number) => {
      const transform = next > 0 ? `translateY(-${next}px)` : '';
      column.style.transform = transform;
      setLift(next);
    };

    const update = () => {
      const card = cardRef.current;
      if (!card) return;
      const previous = column.style.transform;
      column.style.transform = 'none';
      const covered = card.getBoundingClientRect().bottom + KEYBOARD_GAP - vv.height;
      const next = Math.max(0, Math.round(covered));
      if (next === 0) {
        // Keep the raised position through the tap that dismisses the keyboard.
        // Dropping it in the same turn moves the button out from under the finger.
        column.style.transform = previous;
        if (previous && previous !== 'none') {
          window.clearTimeout(settle);
          settle = window.setTimeout(() => apply(0), 200);
        }
        return;
      }
      window.clearTimeout(settle);
      const transform = `translateY(-${next}px)`;
      column.style.transform = transform;
      if (transform === previous) return;
      setLift(next);
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      window.clearTimeout(settle);
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [stage, error]);

  const sendCode = async () => {
    if (busy || !email.includes('@')) return;
    setBusy(true);
    setError(null);
    try {
      await requestLoginCode(email.trim().toLowerCase());
      setStage('code');
    } catch (e) {
      setError(loginErrorMessage(String(e), t, t('login.errGeneric')));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async () => {
    if (busy || code.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      const { token } = await verifyLoginCode(email.trim().toLowerCase(), code);
      storeWebSession(token);
      onSignedIn();
    } catch (e) {
      setError(loginErrorMessage(String(e), t, t('login.errGeneric')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden overscroll-none bg-surface">
      <div
        ref={columnRef}
        className="flex flex-1 flex-col items-center justify-center px-6 pb-10 pt-[calc(var(--tg-safe-top)_+_2rem)]"
        style={lift > 0 ? { transform: `translateY(-${lift}px)` } : undefined}
      >
        <img src={logo} alt="INCPT Wallet" className="splash-float h-20 w-20 object-contain" />
        <h1 className="mt-5 text-[24px] font-semibold text-ink">{t('login.title')}</h1>
        <p className="mt-2 max-w-[20rem] text-center text-[13.5px] leading-[1.55] text-muted">
          {t('login.subtitle')}
        </p>

        <div
          ref={cardRef}
          className="mt-7 w-full max-w-[20rem] rounded-[22px] bg-white p-5 shadow-card"
        >
          {stage === 'email' ? (
            <>
              <label htmlFor="login-email" className="text-[12.5px] text-muted">
                {t('login.email')}
              </label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void sendCode();
                }}
                placeholder="you@example.com"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                className="mt-1.5 w-full rounded-[12px] bg-black/[0.04] px-3.5 py-3 text-[15px] text-ink outline-none placeholder:text-black/25"
              />
              <button
                type="button"
                disabled={busy || !email.includes('@')}
                onClick={() => void sendCode()}
                className="mt-3.5 h-12 w-full rounded-[14px] bg-ink text-[15px] font-semibold text-white active:opacity-90 disabled:opacity-40"
              >
                {busy ? t('common.loading') : t('login.sendCode')}
              </button>
            </>
          ) : (
            <>
              <label htmlFor="login-code" className="text-[12.5px] text-muted">
                {t('login.codeSent', { email })}
              </label>
              <input
                id="login-code"
                inputMode="numeric"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void submitCode();
                }}
                placeholder="000000"
                className="mt-1.5 w-full rounded-[12px] bg-black/[0.04] px-3.5 py-3 text-center font-mono text-[22px] tracking-[6px] text-ink outline-none placeholder:tracking-[6px] placeholder:text-black/20"
              />
              <button
                type="button"
                disabled={busy || code.length !== 6}
                onClick={() => void submitCode()}
                className="mt-3.5 h-12 w-full rounded-[14px] bg-ink text-[15px] font-semibold text-white active:opacity-90 disabled:opacity-40"
              >
                {busy ? t('common.loading') : t('login.signIn')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStage('email');
                  setCode('');
                  setError(null);
                }}
                className="mt-2 h-10 w-full text-[13px] font-medium text-muted"
              >
                {t('common.back')}
              </button>
            </>
          )}

          {error && (
            <p className="mt-3 text-center text-[12.5px] font-semibold text-negative">{error}</p>
          )}
        </div>

        <p className="mt-6 max-w-[20rem] text-center text-[12px] leading-[1.5] text-muted">
          {t('login.readOnlyNote')}
        </p>

        <a
          href={TG_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 text-[13.5px] font-medium text-[#3fa8c6]"
        >
          {t('login.openInTelegram')}
        </a>
      </div>
    </div>
  );
}
